import { ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { CasingGradeTableFiltersResponse, Pagination } from '../../models/response.model';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { CasingGradeTable } from '../../models/casing-grade-table.model';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { SharedDialogComponent } from '../../../../shared/components/shared-dialog/shared-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CustomerAssignService } from '../../../../shared/services/customer-assign.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-casing-grade-table',
  templateUrl: './casing-grade-table.component.html',
  styleUrls: ['./casing-grade-table.component.scss'],
  standalone: false
})
export class CasingGradeTableComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingGradeTable>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public dialog = inject(MatDialog);
  public cdr = inject(ChangeDetectorRef);
  public customerAssignService = inject(CustomerAssignService);
  public route = inject(ActivatedRoute);
  public router = inject(Router);

  filterForm: FormGroup = this.fb.group({
    casingGradeTableCodes: [[]],
    casingGradeTableDescriptions: [[]]
  });
  filterFormBackend: FormGroup = this.fb.group({
    code: [[]],
    description: [[]]
  });
  codesList: string[] = [];
  descriptionList: string[] = [];
  currentCasingGradeTable: CasingGradeTable;
  casingGradeTables: CasingGradeTable[] = [];
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [1, 5, 10, 50, 100];
  displayedRowsForm: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });
  casingGradeTableBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { label: this.translationService.translate('productDataText') },
    { link: '/home/productData/casing-grade-table', label: this.translationService.translate('casingGradeTableText') }
  ];
  additionalColumns = [
    { property: 'casingGradeTableCode', headerKey: this.translationService.translate('casingGradeTableCodeText') },
    { property: 'casingGradeTableDesc', headerKey: this.translationService.translate('casingGradeTableDescText') }
  ];
  isDeleting = false;
  productSaveDisabled = true;

  constructor() {
    this.filterForm = this.createFilterForm();
    this.filterFormBackend = this.createFilterFormBackend();
  }

  private createFilterForm(): FormGroup {
    return this.fb.group({
      casingGradeTableCodes: [[]],
      casingGradeTableDescriptions: [[]]
    });
  }

  private createFilterFormBackend(): FormGroup {
    return this.fb.group({
      code: [[]],
      description: [[]]
    });
  }

  tableConfig: TableConfig<CasingGradeTable> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {},
    onRowClick: (row) => this.navigateToCasingGradeTable(row)
  };

  private navigateToCasingGradeTable(row: CasingGradeTable): void {
    this.router.navigate(['/home/productData/casing-grade-table', row.casingGradeTablePk], {
      state: { code: row.code, description: row.description }
    });
  }

  state = TableConfig.getState(this.tableConfig, this.route);

  formGroup = this.fb.group({
    codes: [this.state.extraFilters?.codes ?? []],
    descriptions: [this.state.extraFilters?.descriptions ?? []]
  });

  fetcher: PaginatorFetcher<CasingGradeTable> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getCasingGradeTables({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        const { casingGradeTables = [], pagination } = response.data;
        this.casingGradeTables = casingGradeTables;
        this.pagination = pagination;
        this.updateDisplayedRowsForm(casingGradeTables);
        return { items: casingGradeTables, count: pagination.totalRecords };
      })
    );
  };

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.loadCasingGradeTableFilters();
    this.handleFilterFormChanges();
    this.updateTableColumnsOnLanguageChange();
  }

  private loadCasingGradeTableFilters(): void {
    this.productDataService.getCasingGradeTableFilters().subscribe((response: CasingGradeTableFiltersResponse) => {
      this.codesList = response.data.casingGradeTableCodes || [];
      this.descriptionList = response.data.casingGradeTableDescriptions || [];
    });
  }

  private handleFilterFormChanges(): void {
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
  }

  private updateTableColumnsOnLanguageChange(): void {
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: { cell: (row) => row.code, header: this.translationService.translate('codeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') }
      };
      this.cdr.markForCheck();
    });
  }

  clearFilters(): void {
    this.codesList = this.codesList || [];
    this.descriptionList = this.descriptionList || [];

    this.filterForm.patchValue({ casingGradeTableCodes: [], casingGradeTableDescriptions: [] }, { emitEvent: false });

    this.generateFilterForm();
    this.tableRef?.patchExtraFilters(this.filterFormBackend.value, true);
    this.cdr.markForCheck();
  }

  createRowForm(row: CasingGradeTable): FormGroup {
    return this.fb.group({
      code: [row.code],
      description: [row.description]
    });
  }

  public updateDisplayedRowsForm(rows: CasingGradeTable[]): void {
    const formArray = this.fb.array(rows.map(this.createRowForm.bind(this)));
    this.displayedRowsForm.setControl('rows', formArray);
  }

  assignToCustomer(item: any): void {
    this.currentCasingGradeTable = item;

    this.customerAssignService.setNavigationData({
      breadcrumbLinks: this.casingGradeTableBreadcrumbs,
      currentDataObject: item,
      additionalColumns: this.additionalColumns,
      searchUri: 'searchCasingGradeTableAssignToCustomer',
      assignUri: 'assignCasingGradeTableToCustomers',
      service: 'backOfficeUrl'
    });

    this.router.navigate(['/home/productData/customer-assign', item.casingGradeTablePk]);
  }

  deleteCasingGradeTable(item: CasingGradeTable): void {
    const dialogRef = this.dialog.open(SharedDialogComponent, {
      width: '550px',
      data: {
        title: this.translationService.translate('warningText'),
        message: this.translationService.translate('deleteCasingGradeTableConfirmText'),
        buttonText: this.translationService.translate('confirmText'),
        cancelButtonText: this.translationService.translate('cancelText')
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isDeleting = true;
      this.cdr.markForCheck();

      this.productDataService.deleteCasingGradeTable(item.casingGradeTablePk).subscribe({
        next: () => this.handleDeleteSuccess(),
        error: (error) => this.handleDeleteError(error)
      });
    });
  }

  private handleDeleteSuccess(): void {
    this.isDeleting = false;
    this.alertService.showAlert({
      content: this.translationService.translate('casingGradeTableDeletedSuccessfully'),
      type: AlertType.success
    } as Alert);
    this.tableRef.resetFilters();
    this.cdr.markForCheck();
  }

  private handleDeleteError(error: any): void {
    this.isDeleting = false;
    this.alertService.showAlert({
      content: error?.error?.message || this.translationService.translate('errorOccurred'),
      type: AlertType.critical
    } as Alert);
    this.cdr.markForCheck();
  }

  createCasingGrade(): void {
    if (this.productSaveDisabled) {
      return;
    }
    this.router.navigate(['/home/productData/casing-grade-table/create']);
  }

  copyCasingGradeTable({ casingGradeTablePk, code, description }: CasingGradeTable): void {
    if (this.productSaveDisabled) {
      return;
    }
    this.router.navigate(['/home/productData/casing-grade-table', casingGradeTablePk, 'copy'], {
      state: { code, description }
    });
  }

  generateFilterForm(): void {
    const { casingGradeTableCodes, casingGradeTableDescriptions } = this.filterForm.value;
    this.filterFormBackend.patchValue({
      code: casingGradeTableCodes.length === this.codesList.length ? [] : casingGradeTableCodes,
      description: casingGradeTableDescriptions.length === this.descriptionList.length ? [] : casingGradeTableDescriptions
    });
  }

  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}
